import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AlertComponent from "../../components/AlertComponent";
import { Context } from "../../Reducers/Store";

function isUserNotifiable(user) {
  if (!user) return false;
  if (user.isActive === false) return false;
  const hasTelegramChat = !!user.telegramChatId || !!user.hasTelegramChat;
  const hasWhatsappPhone = !!user.whatsappPhoneNumber || !!user.hasWhatsappPhone;
  return (
    !!user.notifyEmailOnDeploymentCompletion ||
    (!!user.notifyTelegramOnDeploymentCompletion && hasTelegramChat) ||
    (!!user.notifyWhatsappOnDeploymentCompletion && hasWhatsappPhone)
  );
}

function asBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "sim";
  }
  return false;
}

export default function DeployForm() {
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const canSelectRecipients =
    !!state.profile?.isAdministrator ||
    !!state.profile?.canSelectDeploymentNotificationRecipients;

  const [machines, setMachines] = useState([]);
  const [sites, setSites] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(true);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  const [machineId, setMachineId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [branchName, setBranchName] = useState("");
  const [restaurarBanco, setRestaurarBanco] = useState(false);
  const [agendarDeploy, setAgendarDeploy] = useState(false);
  const [dataAgendamento, setDataAgendamento] = useState("");
  const [observation, setObservation] = useState("");
  const [notificationRecipientIds, setNotificationRecipientIds] = useState([]);
  const [recipientsExpanded, setRecipientsExpanded] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Carrega usuários elegíveis (somente quando o usuário pode selecionar destinatários)
  useEffect(() => {
    if (!canSelectRecipients) {
      setEligibleUsers([]);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API}/deployments/notification-recipient-candidates`)
      .then((res) => {
        const data = (res.data || []).filter(isUserNotifiable);
        setEligibleUsers(data);
      })
      .catch(() => setEligibleUsers([]));
  }, [canSelectRecipients]);

  useEffect(() => {
    if (!agendarDeploy && dataAgendamento) setDataAgendamento("");
  }, [agendarDeploy, dataAgendamento]);

  const siteSelecionado = sites.find((s) => String(s.id) === String(siteId));
  const siteSelecionadoEhProducao = asBool(siteSelecionado?.isProduction);

  // Ao trocar de ambiente, pré-marca os destinatários cadastrados naquele target
  // (exclui o próprio usuário logado — quem dispara o deploy não precisa ser notificado)
  useEffect(() => {
    const recipients = siteSelecionado?.notificationRecipients || [];
    const currentUserId = state.profile?.id;
    setNotificationRecipientIds(
      recipients
        .map((r) => r.userId)
        .filter((id) => id != null && String(id) !== String(currentUserId))
    );
    setRecipientsExpanded(false);
  }, [siteId, siteSelecionado?.notificationRecipients, state.profile?.id]);
  const podeRestaurarBanco =
    !!siteSelecionado &&
    !siteSelecionadoEhProducao &&
    asBool(siteSelecionado.databaseHasRestoreConfiguration) &&
    (!Array.isArray(siteSelecionado.linkedTargets) || siteSelecionado.linkedTargets.length === 0);

  useEffect(() => {
    if (!podeRestaurarBanco && restaurarBanco) setRestaurarBanco(false);
  }, [podeRestaurarBanco, restaurarBanco]);

  // Carrega máquinas ao abrir a tela
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}/machines`)
      .then((res) => {
        const data = (res.data || []).filter((m) => m.isActive !== false);
        data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setMachines(data);
      })
      .catch((err) => {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar máquinas";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoadingMachines(false));
  }, []);

  // Auto-seleciona a única máquina disponível
  useEffect(() => {
    if (machines.length === 1 && !machineId) {
      setMachineId(String(machines[0].id));
    }
  }, [machines, machineId]);

  const maquinaUnica = machines.length === 1;

  // Ao trocar de máquina, limpa seleção dependente e recarrega ambientes daquela máquina
  useEffect(() => {
    setSiteId("");
    setBranchName("");
    setBranches([]);
    if (!machineId) {
      setSites([]);
      return;
    }
    setLoadingSites(true);
    axios
      .get(`${process.env.REACT_APP_API}/deploy-targets/${machineId}`)
      .then((res) => {
        const data = (res.data || []).filter((t) => t.isActive !== false);
        setSites(data);
      })
      .catch((err) => {
        setSites([]);
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar ambientes";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoadingSites(false));
  }, [machineId]);

  // Ao trocar de ambiente, carrega branches do repositório configurado nele
  useEffect(() => {
    if (!siteId) {
      setBranches([]);
      setBranchName("");
      return;
    }
    setLoadingBranches(true);
    setBranchName("");
    axios
      .get(`${process.env.REACT_APP_API}/branches`, { params: { deployTargetId: Number(siteId) } })
      .then((res) => {
        const data = res.data || [];
        setBranches(data);
        const current = data.find((b) => b.isCurrent);
        if (current) setBranchName(current.name);
      })
      .catch((err) => {
        setBranches([]);
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar branches";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoadingBranches(false));
  }, [siteId]);

  const branchSelecionada = branches.find((b) => b.name === branchName);

  const minDataAgendamento = (() => {
    const d = new Date(Date.now() + 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!machineId) {
      AlertComponent("Atenção", "Selecione a máquina", { icon: "warning" });
      return;
    }
    if (!siteId || !branchName) {
      AlertComponent("Atenção", "Selecione o ambiente e a branch", { icon: "warning" });
      return;
    }
    if (agendarDeploy && !dataAgendamento) {
      AlertComponent("Atenção", "Informe a data e hora do agendamento", { icon: "warning" });
      return;
    }
    if (agendarDeploy && dataAgendamento) {
      const scheduled = new Date(dataAgendamento).getTime();
      if (Number.isNaN(scheduled) || scheduled < Date.now() + 60 * 1000) {
        AlertComponent("Atenção", "O agendamento deve ser pelo menos 1 minuto no futuro.", { icon: "warning" });
        return;
      }
    }
    const observationTrimmed = observation.trim();
    if (observationTrimmed.length > 2000) {
      AlertComponent("Atenção", "A observação deve ter no máximo 2000 caracteres.", { icon: "warning" });
      return;
    }

    const payload = {
      branchName,
      deployTargetId: Number(siteId),
      restoreDatabase: podeRestaurarBanco ? restaurarBanco : false,
      observation: observationTrimmed || null,
    };

    if (canSelectRecipients) {
      payload.notificationRecipientUserIds = notificationRecipientIds;
    }

    const isAgendamento = agendarDeploy && dataAgendamento;
    if (isAgendamento) {
      payload.dataAgendamento = `${dataAgendamento}:00-03:00`;
    }

    setSubmitting(true);
    axios
      .post(`${process.env.REACT_APP_API}/deployments`, payload)
      .then(() => {
        AlertComponent(
          "Sucesso",
          isAgendamento ? "Deploy agendado com sucesso" : "Deploy iniciado com sucesso",
          { icon: "success" }
        );
        navigate("/dashboard");
      })
      .catch((err) => {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao iniciar deploy";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div
          className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }}
        />
        <div
          className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }}
        />
        <div className="relative flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition backdrop-blur-sm"
            aria-label="Voltar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <p className="text-purple-300 text-sm font-medium mb-1">Deploy</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Novo Deploy</h1>
            <p className="text-purple-300 text-sm mt-1">Escolha a máquina, o ambiente de destino e a branch a ser publicada</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 p-6 sm:p-8 space-y-6"
      >
        {/* Máquina */}
        <div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12V7a2 2 0 012-2h10a2 2 0 012 2v5M3 19h18M5 12h14a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 012-2zM7 16h.01M11 16h.01" />
            </svg>
            Máquina
          </label>
          <div className="relative">
            <select
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
              disabled={loadingMachines || maquinaUnica}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50/50 pl-4 pr-10 py-3 text-sm text-slate-900
                         focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                         disabled:opacity-60 disabled:cursor-not-allowed"
              required
            >
              <option value="">
                {loadingMachines ? "Carregando máquinas..." : "Selecione uma máquina"}
              </option>
              {machines.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 pointer-events-none">
              {loadingMachines ? (
                <svg className="animate-spin w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </span>
          </div>
        </div>

        {/* Ambiente */}
        <div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12H3l9-9 9 9h-2M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7M5 12l7-7 7 7" />
            </svg>
            Ambiente
          </label>
          <div className="relative">
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              disabled={!machineId || loadingSites}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50/50 pl-4 pr-10 py-3 text-sm text-slate-900
                         focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                         disabled:opacity-60 disabled:cursor-not-allowed"
              required
            >
              <option value="">
                {!machineId
                  ? "Selecione uma máquina primeiro"
                  : loadingSites
                    ? "Carregando ambientes..."
                    : sites.length === 0
                      ? "Nenhum ambiente nesta máquina"
                      : "Selecione um ambiente"}
              </option>
              {sites.map((s) => {
                const nome = s.displayName || s.siteName || `Ambiente #${s.id}`;
                return (
                  <option key={s.id} value={s.id}>
                    {nome}{asBool(s.isProduction) ? " (Produção)" : ""}
                  </option>
                );
              })}
            </select>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 pointer-events-none">
              {loadingSites ? (
                <svg className="animate-spin w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </span>
          </div>
          {siteSelecionado && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {siteSelecionado.siteName && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-600">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                  </svg>
                  {siteSelecionado.siteName}
                </span>
              )}
              {siteSelecionado.applicationPath && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-600 font-mono">
                  {siteSelecionado.applicationPath}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-medium ${
                  siteSelecionadoEhProducao
                    ? "bg-amber-100 text-amber-700"
                    : "bg-emerald-100 text-emerald-700"
                }`}
              >
                {siteSelecionadoEhProducao ? "Produção" : "Homologação"}
              </span>
              {Array.isArray(siteSelecionado.linkedTargets) && siteSelecionado.linkedTargets.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-100 text-indigo-700 font-medium">
                  +{siteSelecionado.linkedTargets.length} site{siteSelecionado.linkedTargets.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
          {siteSelecionado && Array.isArray(siteSelecionado.linkedTargets) && siteSelecionado.linkedTargets.length > 0 && (
            <div className="mt-2 rounded-xl bg-indigo-50 ring-1 ring-indigo-100 px-3 py-2 text-xs text-indigo-700">
              Este deploy tambem publicara: {siteSelecionado.linkedTargets.map((item) => item.displayName || item.siteName).join(", ")}.
            </div>
          )}
        </div>

        {/* Branch */}
        <div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <circle cx="6" cy="3" r="2" />
              <circle cx="6" cy="21" r="2" />
              <circle cx="18" cy="8" r="2" />
              <path d="M6 5v14" />
              <path d="M18 10v2a4 4 0 0 1-4 4H9" />
            </svg>
            Branch
          </label>
          <div className="relative">
            <select
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              disabled={!siteId || loadingBranches}
              className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50/50 pl-4 pr-10 py-3 text-sm text-slate-900
                         focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                         disabled:opacity-60 disabled:cursor-not-allowed"
              required
            >
              <option value="">
                {!siteId
                  ? "Selecione um ambiente primeiro"
                  : loadingBranches
                    ? "Carregando branches..."
                    : branches.length === 0
                      ? "Nenhuma branch disponível"
                      : "Selecione uma branch"}
              </option>
              {branches.map((b) => (
                <option key={b.name} value={b.name}>
                  {b.name}
                  {b.isCurrent ? "  ★ atual" : ""}
                </option>
              ))}
            </select>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 pointer-events-none">
              {loadingBranches ? (
                <svg className="animate-spin w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </span>
          </div>
          {branchSelecionada && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 text-slate-600 font-mono">
                {branchSelecionada.commitSha?.slice(0, 7)}
              </span>
              {branchSelecionada.isCurrent && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-100 text-violet-700 font-medium">
                  Branch atual
                </span>
              )}
            </div>
          )}
        </div>

        {/* Observação */}
        <div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
            <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
            Obs. do deploy
            <span className="normal-case tracking-normal font-medium text-slate-400">(opcional)</span>
          </label>
          <textarea
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Ex.: Nesse deploy está incluído o cálculo de comissão e a correção do pré-cadastro."
            className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 resize-y min-h-[104px]
                       focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition"
          />
          <div className="mt-1 flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500">
            <span>Essa observação aparecerá nas notificações e no detalhe do deploy.</span>
            <span className="tabular-nums shrink-0">{observation.length}/2000</span>
          </div>
        </div>

        {/* Destinatários de notificação (admin ou canSelectDeploymentNotificationRecipients) */}
        {canSelectRecipients && siteSelecionado && (() => {
          const currentUserId = state.profile?.id;
          const preSelectedById = new Map(
            (siteSelecionado.notificationRecipients || [])
              .filter((r) => String(r.userId) !== String(currentUserId))
              .map((r) => [r.userId, r])
          );
          const eligibleById = new Map(
            eligibleUsers
              .filter((u) => String(u.id ?? u.userId) !== String(currentUserId))
              .map((u) => [
                u.id ?? u.userId,
                {
                  userId: u.id ?? u.userId,
                  email: u.email,
                  displayName: u.displayName,
                  notifyEmailOnDeploymentCompletion: !!u.notifyEmailOnDeploymentCompletion,
                  notifyTelegramOnDeploymentCompletion: !!u.notifyTelegramOnDeploymentCompletion,
                  hasTelegramChat: !!u.hasTelegramChat || !!u.telegramChatId,
                  notifyWhatsappOnDeploymentCompletion: !!u.notifyWhatsappOnDeploymentCompletion,
                  hasWhatsappPhone: !!u.hasWhatsappPhone || !!u.whatsappPhoneNumber,
                },
              ])
          );
          // Lista exibida: união (eligibleUsers) + pré-selecionados que não estão em eligibleUsers
          const displayed = [
            ...eligibleById.values(),
            ...[...preSelectedById.values()].filter((r) => !eligibleById.has(r.userId)),
          ];
          if (displayed.length === 0) return null;
          displayed.sort((a, b) => (a.displayName || a.email || "").localeCompare(b.displayName || b.email || ""));

          return (
            <div className="rounded-xl border border-slate-200 bg-slate-50/50">
              <button
                type="button"
                onClick={() => setRecipientsExpanded((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition rounded-xl"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 text-violet-600 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <span className="text-sm font-semibold text-slate-800">Notificar mais pessoas</span>
                  <span className="text-[11px] text-slate-500">
                    {notificationRecipientIds.length} de {displayed.length} selecionado{notificationRecipientIds.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${recipientsExpanded ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {recipientsExpanded && (
                <div className="border-t border-slate-200">
                  <ul className="divide-y divide-slate-100">
                    {displayed.map((r) => {
                      const checked = notificationRecipientIds.includes(r.userId);
                      const isPreSelected = preSelectedById.has(r.userId);
                      return (
                        <li key={r.userId}>
                          <label
                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                              checked ? "bg-violet-50/60" : "hover:bg-white"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setNotificationRecipientIds((prev) =>
                                  e.target.checked
                                    ? [...prev, r.userId]
                                    : prev.filter((id) => id !== r.userId)
                                );
                              }}
                              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className={`text-sm font-semibold truncate ${checked ? "text-violet-900" : "text-slate-800"}`}>
                                  {r.displayName || r.email}
                                </p>
                                {isPreSelected && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-violet-100 text-violet-700 ring-1 ring-violet-200">
                                    Pré-selecionado
                                  </span>
                                )}
                                {r.notifyTelegramOnDeploymentCompletion && r.hasTelegramChat && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-sky-100 text-sky-700 ring-1 ring-sky-200">
                                    Telegram
                                  </span>
                                )}
                                {r.notifyWhatsappOnDeploymentCompletion && r.hasWhatsappPhone && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-lime-100 text-lime-700 ring-1 ring-lime-200">
                                    WhatsApp
                                  </span>
                                )}
                                {r.notifyEmailOnDeploymentCompletion && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                                    E-mail
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500 truncate">{r.email}</p>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100 bg-slate-50">
                    Estes destinatários serão notificados apenas neste deploy. Para alterar a lista padrão do ambiente, edite o ambiente.
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Restaurar banco de dados (indisponível em ambientes de produção) */}
        {podeRestaurarBanco && (
          <label
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition select-none ${
              restaurarBanco
                ? "border-violet-400 bg-violet-50/60 ring-2 ring-violet-100"
                : "border-slate-200 bg-slate-50/50 hover:bg-slate-50"
            }`}
          >
            <input
              type="checkbox"
              checked={restaurarBanco}
              onChange={(e) => setRestaurarBanco(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <ellipse cx="12" cy="5" rx="9" ry="3" />
                  <path d="M3 5v14a9 3 0 0 0 18 0V5" />
                  <path d="M3 12a9 3 0 0 0 18 0" />
                </svg>
                <span className="text-sm font-semibold text-slate-800">Restaurar banco de dados</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Restaura o banco antes de publicar. Indisponível em ambientes de <span className="font-semibold text-slate-700">produção</span>.
              </p>
            </div>
          </label>
        )}

        {/* Agendar Deploy */}
        <label
          className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition select-none ${
            agendarDeploy
              ? "border-violet-400 bg-violet-50/60 ring-2 ring-violet-100"
              : "border-slate-200 bg-slate-50/50 hover:bg-slate-50"
          }`}
        >
          <input
            type="checkbox"
            checked={agendarDeploy}
            onChange={(e) => setAgendarDeploy(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span className="text-sm font-semibold text-slate-800">Agendar Deploy</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Agende a execução deste deploy para uma data e hora específica.
            </p>

            {agendarDeploy && (
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  Data e hora do agendamento
                </label>
                <input
                  type="datetime-local"
                  value={dataAgendamento}
                  onChange={(e) => setDataAgendamento(e.target.value)}
                  min={minDataAgendamento}
                  required
                  className="w-full sm:w-auto rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900
                             focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Horário de Brasília (UTC-03:00).
                </p>
              </div>
            )}
          </div>
        </label>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loadingMachines || loadingSites || loadingBranches || !machineId || !siteId || !branchName || submitting}
            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white
                       shadow-lg shadow-purple-900/30 hover:shadow-purple-900/50 transition-all duration-200
                       hover:-translate-y-0.5 active:translate-y-0
                       disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:shadow-none"
            style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
          >
            {submitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                {agendarDeploy ? "Agendando..." : "Iniciando..."}
              </>
            ) : agendarDeploy ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Agendar Deploy
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Iniciar Deploy
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
